import * as Yup from 'yup';

import { FormField } from '@components';
import {
  ApiEndpoint,
  columnDirection,
  DefaultPort,
  Dictionaries,
  FormActionType,
  FormType,
  hostType,
  InputType,
  OriginEndpoint,
  originType,
  ServiceModalName,
  Translation,
} from '@enums';
import i18n from '@translations';
import type { EndpointType, FormConfig, FormConfigMapping, OriginConfigurationForm, OriginData } from '@types';
import { getOptionListFromCatalog, optionType } from '@utils';

interface GetOriginFormConfigParams {
  endpoint?: EndpointType;
  handleTypeChange?: any;
  values?: OriginConfigurationForm;
  isLoading?: boolean;
  isDisabled?: boolean;
  isHttpsType?: boolean;
  isEdit?: boolean;
  schema: Yup.AnySchema;
  originOptionList?: optionType[];
}

export const getOriginFormConfig = ({
  endpoint = ApiEndpoint.DEFAULT,
  handleTypeChange,
  values,
  isLoading = false,
  isDisabled = false,
  isHttpsType = false,
  isEdit = false,
  schema,
  originOptionList = [],
}: GetOriginFormConfigParams): FormConfig => {
  const tModal: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:modals.origin.${key}`);

  const tForm: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:forms.origin.${key}`);

  const tButton: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:buttons.origin.${key}`);

  const tcButton: (key: string) => string = (key: string): string => i18n.t(`${Translation.Common}:button.${key}`);

  const tValidation: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Validation}:format.${key}`);

  const originsEmptyData: OriginData = {
    origin: '',
    port: values?.type === originType.HTTP ? DefaultPort.HTTP : DefaultPort.HTTPS,
  };

  const getOptionList = (name: Dictionaries) => {
    return getOptionListFromCatalog({ name });
  };

  const ORIGIN_FORM: FormConfigMapping = {
    [ApiEndpoint.DEFAULT]: {
      formType: FormType.CdnOrigin,
      endpoint: ApiEndpoint.DEFAULT,
      formActionType: isEdit ? FormActionType.Edit : FormActionType.Create,
      modalName: ServiceModalName.FormModal,
      label: {
        title: tModal(`${isEdit ? 'editOrigin' : 'createNewOrigin'}`),
        description: tModal(`${isEdit ? 'editOriginDescription' : 'addNewOriginDescription'}`),
        confirmButton: tButton(`${isEdit ? 'editOrigin' : 'addOrigin'}`),
      },
      inputList: [
        {
          name: 'name',
          defaultValue: '',
          inputType: InputType.Text,
          placeholder: tForm('enterYourOriginName'),
          label: tForm('originName'),
          direction: columnDirection.Row,
          required: true,
        },
        {
          name: 'type',
          defaultValue: originType.HTTPS,
          inputType: InputType.Select,
          placeholder: tForm('selectYourScheme'),
          label: tForm('scheme'),
          direction: columnDirection.Row,
          options: getOptionList(Dictionaries.OriginType),
          onChange: handleTypeChange,
          required: true,
        },
        {
          name: 'verify_ssl_cert',
          defaultValue: false,
          inputType: InputType.Switch,
          label: tForm('verifySSL'),
          direction: columnDirection.Row,
          hide: {
            field: 'type',
            condition: (value: string) => value === originType.HTTP,
          },
        },
        {
          name: 'origin_type',
          defaultValue: hostType.Ip,
          inputType: InputType.Select,
          placeholder: tForm('selectYourOriginType'),
          label: tForm('originType'),
          direction: columnDirection.Row,
          options: getOptionList(Dictionaries.HostType),
        },
        {
          name: 'origins',
          defaultValue: '',
          inputType: InputType.MultipleInput,
          label: tForm(`${values?.origin_type === hostType.Ip ? 'ipList' : 'domainList'}`),
          placeholder: tValidation(`${values?.origin_type === hostType.Ip ? 'ip' : 'domain'}`),
          direction: columnDirection.Row,
          nestedName: 'origin',
          nestedComponent: (
            <FormField
              className="w-40"
              name="port"
              withoutLabel
              placeholder={tForm('port')}
              type="number"
              withoutErrorMessage
            />
          ),
          emptyData: originsEmptyData,
          fullWidth: true,
          required: true,
          withoutErrorMessage: true,
        },
        {
          name: 'enable_proxy_host',
          defaultValue: false,
          inputType: InputType.Switch,
          label: tForm('enableOverwriteHostHeader'),
          direction: columnDirection.Row,
        },
        {
          name: 'proxy_host',
          defaultValue: '',
          inputType: InputType.Text,
          label: tForm('overwriteHostHeader'),
          placeholder: tValidation('domain'),
          direction: columnDirection.Row,
          hide: {
            field: 'enable_proxy_host',
            condition: (value: string) => String(value) === 'false',
          },
        },
      ],
      validationSchema: schema,
    },
    [OriginEndpoint.HEALTH_CHECK]: {
      formType: FormType.CdnOrigin,
      endpoint: OriginEndpoint.HEALTH_CHECK,
      modalName: ServiceModalName.FormModal,
      label: {
        title: tModal('healthCheckConfiguration'),
        description: tModal('configureAutomaticHealthChecksToMonitorOriginServerAvailability'),
        confirmButton: tcButton('configure'),
      },
      inputList: [
        {
          name: 'status',
          defaultValue: false,
          inputType: InputType.Switch,
          label: tForm('healthCheck'),
          direction: columnDirection.Row,
          loading: isLoading,
          disabled: isDisabled,
          required: true,
        },
        {
          name: 'protocol',
          defaultValue: isHttpsType ? originType.HTTP : originType.HTTPS,
          inputType: InputType.Select,
          options: originOptionList,
          label: tForm('protocol'),
          direction: columnDirection.Row,
          loading: isLoading,
          disabled: isDisabled,
          required: true,
        },
        {
          name: 'host',
          defaultValue: '',
          inputType: InputType.Text,
          label: tForm('httpRequestHost'),
          placeholder: tValidation('host'),
          direction: columnDirection.Row,
          loading: isLoading,
          disabled: isDisabled,
          required: true,
          hide: {
            field: 'protocol',
            condition: (value: string) => value === 'tcp',
          },
        },
        {
          name: 'uri',
          defaultValue: '/',
          inputType: InputType.Text,
          label: tForm('httpRequestUri'),
          placeholder: '/status',
          loading: isLoading,
          disabled: isDisabled,
          direction: columnDirection.Row,
          required: true,
          hide: {
            field: 'protocol',
            condition: (value: string) => value === 'tcp',
          },
        },
        {
          name: 'verify_ssl_cert',
          defaultValue: false,
          inputType: InputType.Switch,
          label: tForm('verifySSL'),
          loading: isLoading,
          disabled: isDisabled,
          direction: columnDirection.Row,
          hide: {
            field: 'protocol',
            condition: (value: string) => value !== originType.HTTPS,
          },
        },
      ],
      validationSchema: schema,
    },
  };

  return (ORIGIN_FORM[endpoint] ?? ORIGIN_FORM[ApiEndpoint.DEFAULT]) as FormConfig;
};
